// MemoryMagnetsDetails.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, CloseIcon } from '../../../public/icons';
import Pagination from '../../../component/shared/Pagination';

const MemoryMagnetsDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    collectionName,
    collectionDescription,
    productFamily = 'Memory Magnets'
  } = location.state || {
    productFamily: 'Memory Magnets',
    collectionName: 'Nigeria — Travel Memory Magnet',
    collectionDescription:
      'A collection of memory magnets inspired by iconic places across Nigeria, each one telling a story of culture, history, and meaning.'
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const [filters, setFilters] = useState({
    availability: {
      inStock: false,
      outOfStock: false
    }
  });

  const allCollections = {
    'Nigeria — Travel Memory Magnet': {
      magnets: [
        {
          id: 'yankari',
          title: 'Yankari Game Reserve',
          description:
            "Wildlife, nature, and the stories that live in Nigeria's most iconic reserve.",
          image:
            'https://images.unsplash.com/photo-1550358864-518f202c02ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Bauchi State'
        },
        {
          id: 'obudu',
          title: 'Obudu Mountain Resort',
          description:
            "Wildlife, nature, and the stories that live in Nigeria's most iconic reserve.",
          image:
            'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Cross River State'
        },
        {
          id: 'olumo',
          title: 'Olumo Rock',
          description:
            'The ultimate memory gift. Combine physical keepsakes with living digital memories.',
          image:
            'https://images.unsplash.com/photo-1518837695005-2083093ee35b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Ogun State'
        },
        {
          id: 'idanre',
          title: 'Idanre Hills',
          description:
            'A photo-book-like collection of magnetic memories. Perfect for family history and milestones.',
          image:
            'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: false,
          location: 'Ondo State'
        },
        {
          id: 'erin-ijesha',
          title: 'Erin Ijesha Waterfalls',
          description:
            'A photo-book-like collection of magnetic memories. Perfect for family history and milestones.',
          image:
            'https://images.unsplash.com/photo-1439066615861-d1af74d74000?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Osun State'
        },
        {
          id: 'zuma',
          title: 'Zuma Rock',
          description:
            'A photo-book-like collection of magnetic memories. Perfect for family history and milestones.',
          image:
            'https://images.unsplash.com/photo-1448376561459-dbe8868fa34c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Niger State'
        }
      ],
      subtitle: 'Nigeria Tourism collections'
    },
    'Paris — Travel Memory Magnet': {
      magnets: [
        {
          id: 'eiffel',
          title: 'Eiffel Tower',
          description: 'Iconic Paris landmark and symbol of France.',
          image:
            'https://cdn.britannica.com/31/255531-050-B7E07090/eiffel-tower-paris-france-champ-de-mars-view.jpg',
          category: 'travelPlaces',
          inStock: true,
          location: 'Paris, France'
        },
        {
          id: 'louvre',
          title: 'Louvre Museum',
          description: "The world's largest art museum and historic monument.",
          image:
            'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Paris, France'
        },
        {
          id: 'notre-dame',
          title: 'Notre-Dame Cathedral',
          description:
            'Medieval Catholic cathedral known for its French Gothic architecture.',
          image:
            'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Paris, France'
        }
      ],
      subtitle: 'Paris Travel Collections'
    },
    'Canada — Travel Memory Magnet': {
      magnets: [
        {
          id: 'niagara',
          title: 'Niagara Falls',
          description:
            'Famous waterfalls on the border between Canada and the USA.',
          image:
            'https://images.unsplash.com/photo-1517935706615-2717063c2225?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Ontario, Canada'
        },
        {
          id: 'cn-tower',
          title: 'CN Tower',
          description:
            'Communications and observation tower in downtown Toronto.',
          image:
            'https://images.unsplash.com/photo-1512544783981-e6e6d0bc1a1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Toronto, Canada'
        }
      ],
      subtitle: 'Canada Travel Collections'
    },
    'India — Travel Memory Magnet': {
      magnets: [
        {
          id: 'taj-mahal',
          title: 'Taj Mahal',
          description: 'Iconic white marble mausoleum in Agra.',
          image:
            'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Agra, India'
        },
        {
          id: 'gateway-india',
          title: 'Gateway of India',
          description: 'Monument built during the British Raj in Mumbai.',
          image:
            'https://images.unsplash.com/photo-1597040663342-45b6af3d91a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Mumbai, India'
        }
      ],
      subtitle: 'India Travel Collections'
    },
    'London — Travel Memory Magnet': {
      magnets: [
        {
          id: 'big-ben',
          title: 'Big Ben',
          description:
            'The Great Bell of the clock at the north end of the Palace of Westminster.',
          image:
            'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'London, UK'
        },
        {
          id: 'london-eye',
          title: 'London Eye',
          description:
            'Giant Ferris wheel on the South Bank of the River Thames.',
          image:
            'https://images.unsplash.com/photo-1583484963886-cfe2bff2945f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'London, UK'
        }
      ],
      subtitle: 'London Travel Collections'
    },
    'Hawaii — Travel Memory Magnet': {
      magnets: [
        {
          id: 'waikiki',
          title: 'Waikiki Beach',
          description: 'Famous beach neighborhood in Honolulu.',
          image:
            'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: true,
          location: 'Honolulu, Hawaii'
        },
        {
          id: 'volcano',
          title: 'Hawaii Volcanoes National Park',
          description: "Home to two of the world's most active volcanoes.",
          image:
            'https://images.unsplash.com/photo-1544551763-77a457bc5b57?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'travelPlaces',
          inStock: false,
          location: 'Hawaii, USA'
        }
      ],
      subtitle: 'Hawaii Travel Collections'
    },
    'Names & their meaning Magnet': {
      magnets: [
        {
          id: 'names-and-meaning',
          title: 'African Names Collection',
          description: 'Traditional African names and their meanings.',
          image:
            'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'namesAndMeaning',
          inStock: true,
          location: 'Various Origins'
        },
        {
          id: 'names-and-meaning',
          title: 'Biblical Names Collection',
          description: 'Biblical names with spiritual significance.',
          image:
            'https://images.unsplash.com/photo-1544717305-99670f9c28f4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          category: 'namesAndMeaning',
          inStock: true,
          location: 'Religious Origins'
        }
      ],
      subtitle: 'Names & Meaning Collections'
    }
  };

  const handleIndividualMagnetClick = (magnet) => {
    navigate('/memory-magnets/edit-template', {
      state: {
        id: magnet.id,
        isCustom: false,
        magnetId: magnet.id,
        productFamily, // Passes the correct family (e.g., Heritage Magnet)
        title: magnet.title,
        collectionTitle: collectionName,
        description: magnet.description,
        image: magnet.image,
        location: magnet.location,
        inStock: magnet.inStock,
        price: 30000
      }
    });
  };

  const currentCollection =
    allCollections[collectionName] ||
    allCollections['Nigeria — Travel Memory Magnet'];
    
  const magnets = currentCollection.magnets;
  const collectionSubtitle = currentCollection.subtitle;

  // Use productFamily for the main title if it's a specific category like Heritage
  const displayTitle = productFamily !== 'Memory Magnets' ? productFamily : collectionName;

  const handleFilterChange = (category, subcategory) => {
    setFilters((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: !prev[category][subcategory]
      }
    }));
  };

  const clearFilters = () => {
    setFilters({
      availability: {
        inStock: false,
        outOfStock: false
      }
    });
  };

  const filteredMagnets = useMemo(() => {
    return magnets.filter((magnet) => {
      if (
        searchQuery &&
        !magnet.title.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      const availabilityFilters = filters.availability;
      if (availabilityFilters.inStock || availabilityFilters.outOfStock) {
        if (availabilityFilters.inStock && !magnet.inStock) return false;
        if (availabilityFilters.outOfStock && magnet.inStock) return false;
      }

      return true;
    });
  }, [magnets, searchQuery, filters]);

  const totalItems = filteredMagnets.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMagnets = filteredMagnets.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = () => {
    return Object.values(filters.availability).some(Boolean);
  };

  const getHoverMessage = (magnet, collectionName) => {
    const title = magnet.title;
    const loc = magnet.location;

    if (collectionName.includes('Nigeria')) {
      if (title.includes('Yankari'))
        return 'Yankari Game Reserve is a wildlife paradise';
      if (title.includes('Obudu'))
        return 'Obudu Mountain Resort offers breathtaking views';
      if (title.includes('Olumo'))
        return 'Olumo Rock tells ancient stories';
      if (title.includes('Idanre'))
        return 'Idanre Hills hold historical treasures';
      if (title.includes('Erin Ijesha'))
        return "Erin Ijesha Waterfalls is nature's wonder";
      if (title.includes('Zuma'))
        return 'Zuma Rock is an iconic landmark';
      return `${title} in ${loc} is beautiful`;
    }

    if (collectionName.includes('Paris')) {
      if (title.includes('Eiffel'))
        return 'Eiffel Tower is a Parisian masterpiece';
      if (title.includes('Louvre'))
        return 'Louvre Museum holds artistic treasures';
      if (title.includes('Notre-Dame'))
        return 'Notre-Dame Cathedral is architectural beauty';
      return `${title} in Paris is magnificent`;
    }

    if (title.includes('Niagara')) return 'Niagara Falls is a natural wonder';
    if (title.includes('CN Tower')) return 'CN Tower offers stunning city views';
    if (title.includes('Taj Mahal'))
      return 'Taj Mahal is a symbol of eternal love';
    if (title.includes('Gateway'))
      return 'Gateway of India is a historic monument';
    if (title.includes('Big Ben'))
      return "Big Ben is London's iconic timekeeper";
    if (title.includes('London Eye'))
      return 'London Eye provides breathtaking views';
    if (title.includes('Waikiki'))
      return 'Waikiki Beach is a tropical paradise';
    if (title.includes('Volcano'))
      return "Hawaii Volcanoes are nature's power";
    if (title.includes('African Names'))
      return 'African names carry deep meanings';
    if (title.includes('Biblical Names'))
      return 'Biblical names have spiritual significance';

    return `${title} in ${loc} is beautiful`;
  };

  return (
    <div className="min-h-screen bg-[#FCF7EE] relative">
      <section className="py-6 md:py-10">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl text-left">
          
          {/* ✅ BREADCRUMB */}
          <nav className="flex items-center space-x-3 text-base mb-8">
            <Link to="/shop" className="text-[#6B635D] hover:text-[#1C2A46] transition-colors">
              Shop
            </Link>
            <span className="text-[#6B635D]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </span>
            <span className="text-[#1C2A46] font-medium">
              {displayTitle}
            </span>
          </nav>

          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#1C2A46] mb-4 md:mb-6">
              {displayTitle}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-[#6B635D] font-normal mb-6 md:mb-8 max-w-2xl">
              {collectionDescription}
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 md:gap-4">
            <div className="w-full md:w-1/2 mb-4 md:mb-0">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-[#1C2A46] mb-2 md:mb-4">
                {collectionSubtitle}
              </h2>
              <p className="text-sm sm:text-base text-[#6B635D] max-w-3xl">
                Every Kapta piece is a vessel for memory designed to hold
                voices, moments, and meaning.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-[280px] md:w-[320px] lg:w-[355px]">
                <input
                  type="text"
                  placeholder="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="p-3 pr-12 border w-full border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={20} />
                </div>
              </div>

              <button
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-[#DBDBDB] bg-transparent hover:bg-[#F8F8F8] transition-colors w-full sm:w-auto"
              >
                <FilterIcon />
                <span className="text-[#1C2A46]">Filter</span>
                {hasActiveFilters() && (
                  <span className="ml-1 w-2 h-2 bg-[#55000C] rounded-full"></span>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          {paginatedMagnets.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {paginatedMagnets.map((magnet) => (
                <div
                  key={magnet.id}
                  className="group cursor-pointer"
                  onClick={() => handleIndividualMagnetClick(magnet)}
                >
                  <div className="h-full flex flex-col">
                    <div className="aspect-square mb-4 md:mb-5 overflow-hidden relative">
                      <div className="flip-card w-full h-full">
                        <div className="flip-card-front w-full h-full">
                          <img
                            src={magnet.image}
                            alt={magnet.title}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="flip-card-back w-full h-full bg-[#2B2A2A] flex items-center justify-center p-4 md:p-6">
                          <div className="text-center">
                            <p className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-medium leading-tight">
                              {getHoverMessage(magnet, collectionName)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 text-left mb-4 md:mb-8">
                      <h3 className="text-base sm:text-lg md:text-xl font-medium text-[#1C2A46] mb-1">
                        {magnet.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#6B635D] mb-1 md:mb-2 font-medium">
                        {magnet.location}
                      </p>
                      <p className="text-sm sm:text-base text-[#6B635D] leading-relaxed">
                        {magnet.description}
                      </p>
                    </div>

                    {!magnet.inStock && (
                      <div className="mt-2 md:mt-4 text-red-600 text-xs sm:text-sm font-medium">
                        Out of Stock
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {paginatedMagnets.length === 0 && (
            <div className="text-center py-12 md:py-20">
              <p className="text-[#6B635D] text-base md:text-lg">
                No magnets found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  clearFilters();
                }}
                className="mt-4 text-[#55000C] underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-12 md:mt-16">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </section>

      {isFilterOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsFilterOpen(false)}
          />

          <div className="fixed right-0 top-0 h-full w-full sm:w-[80%] md:w-[40%] lg:w-[35%] bg-[#F9F6F2] z-50 overflow-y-auto">
            <div className="p-4 sm:p-6 h-full flex flex-col">
              <div className="flex items-center justify-between">
                <h3 className="text-xl sm:text-2xl font-medium text-[#1C2A46]">
                  Filter Items
                </h3>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="p-1 hover:bg-[#F8F8F8] border border-[#DBDBDB]"
                >
                  <CloseIcon />
                </button>
              </div>

              <p className="text-sm text-[#6B635D] mb-6 sm:mb-8 text-left">
                Narrow your search to find the memories that matter to you.
              </p>

              <div className="mb-6 sm:mb-8">
                <h4 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-3 sm:mb-4 text-left">
                  Availability
                </h4>
                <div className="space-y-2 sm:space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.availability.inStock}
                      onChange={() =>
                        handleFilterChange('availability', 'inStock')
                      }
                      className="w-4 h-4 text-[#D4A574] border-[#DBDBDB] rounded focus:ring-[#D4A574]"
                    />
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      In Stock
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.availability.outOfStock}
                      onChange={() =>
                        handleFilterChange('availability', 'outOfStock')
                      }
                      className="w-4 h-4 text-[#D4A574] border-[#DBDBDB] rounded focus:ring-[#D4A574]"
                    />
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Out of Stock
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-auto pt-4 sm:pt-6 pb-4 sm:pb-6 border-t border-[#DBDBDB]">
                <div className="flex gap-3">
                  {hasActiveFilters() ? (
                    <>
                      <button
                        onClick={clearFilters}
                        className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 border border-[#DBDBDB] text-[#1C2A46] hover:bg-[#F8F8F8] transition-colors text-sm sm:text-base"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => setIsFilterOpen(false)}
                        className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 bg-[#D2A146] text-white hover:bg-[#C49660] transition-colors text-sm sm:text-base"
                      >
                        Apply Filter
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsFilterOpen(false)}
                      className="w-full py-2.5 sm:py-3 px-3 sm:px-4 bg-[#D2A146] text-white hover:bg-[#C49660] transition-colors text-sm sm:text-base"
                    >
                      Apply Filter
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .flip-card {
          perspective: 1000px;
          position: relative;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
          border-radius: 2px;
        }
        .flip-card-front { transform: rotateY(0deg); }
        .flip-card-back {
          transform: rotateY(180deg);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .group:hover .flip-card-front { transform: rotateY(-180deg); }
        .group:hover .flip-card-back { transform: rotateY(0deg); }
        @media (max-width: 768px) {
          .group:active .flip-card-front { transform: rotateY(-180deg); }
          .group:active .flip-card-back { transform: rotateY(0deg); }
        }
      `}</style>
    </div>
  );
};

export default MemoryMagnetsDetails;